// src/pages/ContinentsPage.js
import React, { useState, useEffect } from 'react';
import styles from './ContinentsPage.module.css';
import apiClient from '../services/ApiClient';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatusToggle from '../components/common/StatusToggle';
import ContinentNamesModal from '../components/continents/ContinentNamesModal';
import { readApiError } from '../services/catalogApi';
import { toast } from 'react-toastify';

// Sunucunun kabul ettiği uzantılar; seçim anında eleyip boş yere istek atmıyoruz.
const ALLOWED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'svg'];
const IMAGE_ACCEPT = '.jpg,.jpeg,.png,.webp,.svg';

const ContinentsPage = () => {
    const [continents, setContinents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [savingStates, setSavingStates] = useState({});
    const [imageBusy, setImageBusy] = useState({}); // { continentId: bool }
    const [editingNames, setEditingNames] = useState(null); // adları düzenlenen kıta

    // Kıta verilerini API'den çek
    const fetchContinents = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await apiClient.get('/continents');
            
            if (response.data && response.data.status === 'success' && Array.isArray(response.data.data)) {
                // API sırası rastgele geliyor — ID'ye göre sabitle
                setContinents([...response.data.data].sort((a, b) => a.id - b.id));
            } else {
                throw new Error("API'den beklenen formatta veri gelmedi.");
            }
        } catch (err) {
            console.error('Kıtalar yüklenirken hata:', err);
            setError('Kıtalar yüklenirken bir hata oluştu.');
            setContinents([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchContinents();
    }, []);

    const handleToggleStatus = async (continentId, statusType, currentStatus) => {
        const stateKey = `${continentId}-${statusType}`;
        setSavingStates(prev => ({ ...prev, [stateKey]: true }));
        
        try {
            const newStatus = !currentStatus;
            
            // Optimistic update
            setContinents(prevContinents =>
                prevContinents.map(continent =>
                    continent.id === continentId
                        ? { ...continent, [statusType]: newStatus }
                        : continent
                )
            );

            // API'ye güncelleme gönder
            await apiClient.patch(`/continents/${continentId}`, {
                [statusType]: newStatus
            });

            const continentName = continents.find(c => c.id === continentId)?.name;
            const statusText = statusType === 'isActiveLifetime' ? 'Ömürlük ESim' : 'Kıtalar Arası ESim';
            toast.success(`${continentName} kıtası ${statusText} paketleri ${newStatus ? 'aktif' : 'pasif'} yapıldı.`);

        } catch (err) {
            console.error('Kıta durumu güncellenirken hata:', err);
            toast.error('Kıta durumu güncellenirken bir hata oluştu.');
            
            // Hata durumunda geri al
            setContinents(prevContinents =>
                prevContinents.map(continent =>
                    continent.id === continentId
                        ? { ...continent, [statusType]: currentStatus }
                        : continent
                )
            );
        } finally {
            setSavingStates(prev => ({ ...prev, [stateKey]: false }));
        }
    };

    const handleImageSelect = async (continent, event) => {
        const input = event.target;
        const file = input.files?.[0];
        input.value = ''; // aynı dosya art arda seçilebilsin
        if (!file) return;

        const extension = (file.name.split('.').pop() || '').toLowerCase();
        if (!ALLOWED_IMAGE_EXTENSIONS.includes(extension)) {
            toast.error(`Desteklenmeyen dosya türü. İzin verilenler: ${ALLOWED_IMAGE_EXTENSIONS.join(', ')}.`);
            return;
        }

        setImageBusy(prev => ({ ...prev, [continent.id]: true }));
        try {
            const form = new FormData();
            form.append('image', file);

            const response = await apiClient.post(`/continents/${continent.id}/image`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            // Yanıt güncel kaydı taşır; listeyi ayrıca tazelemeye gerek yok.
            const imgUrl = response.data?.data?.imgUrl ?? null;
            setContinents(prev => prev.map(c => (c.id === continent.id ? { ...c, imgUrl } : c)));
            toast.success(`${continent.name} görseli güncellendi.`);
        } catch (err) {
            console.error('Kıta görseli yüklenirken hata:', err);
            toast.error(readApiError(err, 'Görsel yüklenirken sunucuda bir hata oluştu.').message);
            // 404 = kıta artık yok; elimizdeki liste bayat demektir.
            if (err?.response?.status === 404) fetchContinents();
        } finally {
            setImageBusy(prev => ({ ...prev, [continent.id]: false }));
        }
    };

    const handleRemoveImage = async (continent) => {
        if (!window.confirm(`${continent.name} kıtasının görseli kaldırılsın mı?`)) return;

        setImageBusy(prev => ({ ...prev, [continent.id]: true }));
        try {
            // Boş string "görseli kaldır" demek; alanı hiç göndermemek "dokunma" demek.
            await apiClient.patch(`/continents/${continent.id}`, { imgUrl: '' });

            setContinents(prev => prev.map(c => (c.id === continent.id ? { ...c, imgUrl: null } : c)));
            toast.success(`${continent.name} görseli kaldırıldı.`);
        } catch (err) {
            console.error('Kıta görseli kaldırılırken hata:', err);
            toast.error(readApiError(err, 'Görsel kaldırılırken bir hata oluştu.').message);
            if (err?.response?.status === 404) fetchContinents();
        } finally {
            setImageBusy(prev => ({ ...prev, [continent.id]: false }));
        }
    };

    const activeLifetimeCount = continents.filter(c => c.isActiveLifetime).length;
    const activeIntercontinentalCount = continents.filter(c => c.isActiveIntercontinental).length;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerText}>
                    <h1 className={styles.title}>
                        Kıtalar Yönetimi
                        {!isLoading && !error && <span className={`${styles.count} num`}>{continents.length}</span>}
                    </h1>
                    <p className={styles.subtitle}>
                        Kıta bazında eSIM paket yayınını yönetin. Anahtarı açtığınızda değişiklik anında kaydedilir.
                    </p>
                </div>
            </div>

            {isLoading && (
                <div className={styles.loadingContainer}>
                    <LoadingSpinner />
                </div>
            )}

            {!isLoading && error && (
                <div className={styles.errorPanel}>
                    <p className={styles.errorPanelText}>Veriler alınamadı. Bağlantıyı kontrol edip tekrar deneyin.</p>
                    <button type="button" onClick={fetchContinents} className={`${styles.retryButton} ghost`}>
                        Tekrar dene
                    </button>
                </div>
            )}

            {!isLoading && !error && (
            <div className={styles.contentArea}>
                <div className={styles.statsContainer}>
                    <div className={styles.statCard}>
                        <h3>Toplam Kıta</h3>
                        <p className={styles.statNumber}>{continents.length}</p>
                    </div>
                    <div className={styles.statCard}>
                        <h3>Ömürlük ESim Aktif</h3>
                        <p className={styles.statNumber}>
                            {activeLifetimeCount}<span className={styles.statTotal}>/{continents.length}</span>
                        </p>
                    </div>
                    <div className={styles.statCard}>
                        <h3>Kıtalar Arası Aktif</h3>
                        <p className={styles.statNumber}>
                            {activeIntercontinentalCount}<span className={styles.statTotal}>/{continents.length}</span>
                        </p>
                    </div>
                </div>

                <div className={styles.tableContainer}>
                    <table className={styles.continentsTable}>
                        <thead>
                            <tr>
                                <th className={styles.idHead}>ID</th>
                                <th className={styles.imageHead}>Görsel</th>
                                <th className={styles.nameHead}>Kıta Adı</th>
                                <th>Ömürlük ESim Paketleri</th>
                                <th>Kıtalar Arası ESim Paketleri</th>
                                <th className={styles.actionsHead}>İşlemler</th>
                            </tr>
                        </thead>
                        <tbody>
                            {continents.map(continent => {
                                const busy = !!imageBusy[continent.id];
                                return (
                                <tr key={continent.id} className={styles.tableRow}>
                                    <td className={styles.idCol}><span className={`${styles.idCell} num`}>{continent.id}</span></td>
                                    <td className={styles.imageCol}>
                                        {continent.imgUrl ? (
                                            <img
                                                src={continent.imgUrl}
                                                alt={`${continent.name} görseli`}
                                                className={styles.continentImage}
                                            />
                                        ) : (
                                            <span className={styles.imagePlaceholder} title="Görsel yüklenmemiş">—</span>
                                        )}
                                    </td>
                                    <td className={styles.nameCell}>{continent.name}</td>
                                    <td className={styles.statusCell}>
                                        <StatusToggle
                                            label={`${continent.name} — Ömürlük ESim paketleri`}
                                            checked={continent.isActiveLifetime}
                                            saving={savingStates[`${continent.id}-isActiveLifetime`]}
                                            disabled={busy}
                                            onChange={() => handleToggleStatus(continent.id, 'isActiveLifetime', continent.isActiveLifetime)}
                                        />
                                    </td>
                                    <td className={styles.statusCell}>
                                        <StatusToggle
                                            label={`${continent.name} — Kıtalar arası ESim paketleri`}
                                            checked={continent.isActiveIntercontinental}
                                            saving={savingStates[`${continent.id}-isActiveIntercontinental`]}
                                            disabled={busy}
                                            onChange={() => handleToggleStatus(continent.id, 'isActiveIntercontinental', continent.isActiveIntercontinental)}
                                        />
                                    </td>
                                    <td className={styles.actionsCell}>
                                        <div className={styles.actionButtons}>
                                            <button
                                                type="button"
                                                className={`${styles.removeButton} ghost`}
                                                onClick={() => setEditingNames(continent)}
                                            >
                                                Adları Düzenle
                                            </button>
                                            <input
                                                type="file"
                                                id={`continent-image-${continent.id}`}
                                                className={styles.fileInput}
                                                accept={IMAGE_ACCEPT}
                                                disabled={busy}
                                                onChange={(e) => handleImageSelect(continent, e)}
                                            />
                                            <label
                                                htmlFor={`continent-image-${continent.id}`}
                                                className={`${styles.fileInputLabel} ${busy ? styles.fileInputLabelBusy : ''}`}
                                            >
                                                {busy ? 'Yükleniyor…' : continent.imgUrl ? 'Görseli Değiştir' : 'Görsel Yükle'}
                                            </label>
                                            {continent.imgUrl && (
                                                <button
                                                    type="button"
                                                    className={`${styles.removeButton} ghost`}
                                                    disabled={busy}
                                                    onClick={() => handleRemoveImage(continent)}
                                                >
                                                    Kaldır
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            )}

            <ContinentNamesModal
                isOpen={!!editingNames}
                continent={editingNames}
                onClose={() => setEditingNames(null)}
                onSaved={() => {
                    setEditingNames(null);
                    fetchContinents();
                }}
            />
        </div>
    );
};

export default ContinentsPage; 